import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SelectInput } from "@/components/ui/SelectInput";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { formatDateWithoutTimezoneToFormat } from "@/lib/api/date";
import { Trader } from "@/lib/api/reports";
import { TradingAccount } from "@/lib/api/otc";
import {
  CreateOtcErrorCorrectionInput,
  CreateOtcErrorCorrectionValidation,
  useCreateOtcErrorCorrection,
} from "@/lib/api/otcErrorCorrection";
import {
  OtcErrorCorrectionForm,
  OtcErrorCorrectionFormErrors,
  emptyErrorCorrectionValues,
} from "./OtcErrorCorrectionForm";

/** Maps zod issues onto the per-field error map used by the form. */
function issuesToErrors(
  issues: { path: (string | number)[]; message: string }[],
): OtcErrorCorrectionFormErrors {
  const errors: OtcErrorCorrectionFormErrors = {};
  for (const issue of issues) {
    const key = issue.path[0];
    if (typeof key === "string" && !(key in errors)) {
      errors[key as keyof OtcErrorCorrectionFormErrors] = issue.message;
    }
  }
  return errors;
}

interface CreateErrorCorrectionModalProps {
  open: boolean;
  traders: Trader[];
  accounts: TradingAccount[];
  /**
   * Optional contract/layer context. When creating from a contract's layer
   * these are set on the new correction; the standalone create leaves them null.
   */
  tradingContractId?: string | null;
  tradingContractLayerId?: string | null;
  tradingAccountId?: string | null;
  /**
   * When provided, a required layer dropdown is shown and the picked layer id
   * is set as `trading_contract_layer_id`. Used by the contract-detail entry
   * point so the correction is tied to a specific layer.
   */
  layerOptions?: { value: string; label: string }[];
  onClose: () => void;
  onCreated: () => void;
}

export function CreateErrorCorrectionModal({
  open,
  traders,
  accounts,
  tradingContractId = null,
  tradingContractLayerId = null,
  tradingAccountId = null,
  layerOptions,
  onClose,
  onCreated,
}: CreateErrorCorrectionModalProps) {
  const createErrorCorrection = useCreateOtcErrorCorrection();

  const [values, setValues] = useState<CreateOtcErrorCorrectionInput>(
    emptyErrorCorrectionValues(),
  );
  const [errors, setErrors] = useState<OtcErrorCorrectionFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [layerError, setLayerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Seed fresh values each time the modal opens. Default `date` to today,
  // computed the same way it is serialized on submit.
  useEffect(() => {
    if (!open) return;
    const seeded = emptyErrorCorrectionValues();
    seeded.date = formatDateWithoutTimezoneToFormat(new Date(), "YYYY-MM-DD");
    seeded.trading_contract_id = tradingContractId;
    seeded.trading_contract_layer_id = tradingContractLayerId;
    seeded.trading_account_id = tradingAccountId ?? "";
    setValues(seeded);
    setErrors({});
    setSubmitError(null);
    setLayerError(null);
  }, [open, tradingContractId, tradingContractLayerId, tradingAccountId]);

  const handleSubmit = async () => {
    if (isSaving) return;
    setSubmitError(null);

    if (layerOptions && !values.trading_contract_layer_id) {
      setLayerError("Layer is required");
      return;
    }
    setLayerError(null);

    const parsed = CreateOtcErrorCorrectionValidation.safeParse(values);
    if (!parsed.success) {
      setErrors(issuesToErrors(parsed.error.issues));
      return;
    }
    setErrors({});

    setIsSaving(true);
    try {
      const result = await createErrorCorrection(parsed.data);
      if (!result.success) {
        setSubmitError(result.message || "Failed to create error correction.");
        return;
      }
      onCreated();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (isSaving) return;
    if (!next) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-none w-[60vw] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Create Error Correction</DialogTitle>
        </DialogHeader>

        <div className="grow overflow-y-auto pr-1 flex flex-col gap-4">
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to create</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          {layerOptions && (
            <div className="grid items-center gap-1.5">
              <Label className=""><span className="font-semibold">Layer</span> <span className="text-xs text-muted-foreground">(Optional)</span></Label>
              <SelectInput
                value={values.trading_contract_layer_id ?? ""}
                options={layerOptions}
                onValueChange={(value: string) =>
                  setValues((prev) => ({
                    ...prev,
                    trading_contract_layer_id: value || null,
                  }))
                }
                disabled={isSaving}
              />
              {layerError && (
                <span className="text-xs text-destructive">{layerError}</span>
              )}
            </div>
          )}

          <div className="rounded-lg border p-4">
            <OtcErrorCorrectionForm
              values={values}
              onChange={setValues}
              traders={traders}
              accounts={accounts}
              errors={errors}
            />
          </div>
        </div>

        <DialogFooter className="shrink-0">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
